import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Lock } from "lucide-react";
import api, { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";

const MONTHS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

export function DynamicPricingPanel({ onUpgrade }) {
  const { t } = useTranslation();
  const [available, setAvailable] = useState(true);
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/loueur/dynamic-pricing").then((r) => { setAvailable(r.data.available); setCfg(r.data.config); }).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try { const { data } = await api.put("/loueur/dynamic-pricing", cfg); setCfg(data.config); toast.success(t("pricing.saved")); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    setSaving(false);
  };

  if (!cfg) return null;
  if (!available) return (
    <div data-testid="pricing-locked" className="text-center py-12 bg-white rounded-2xl border border-border">
      <Lock className="w-8 h-8 text-canard mx-auto mb-3" />
      <p className="text-muted2 font-body mb-4">{t("pricing.locked")}</p>
      <Button data-testid="pricing-upgrade" onClick={onUpgrade} className="rounded-full bg-canard hover:bg-canard-dark font-bold">{t("litiges.upgrade")}</Button>
    </div>
  );

  const toggleMonth = (field, m) => {
    const arr = cfg[field] || [];
    setCfg({ ...cfg, [field]: arr.includes(m) ? arr.filter((x) => x !== m) : [...arr, m] });
  };
  const MonthPicker = ({ field }) => (
    <div className="flex flex-wrap gap-1 mt-1">
      {MONTHS.map((m) => (
        <button key={m} type="button" onClick={() => toggleMonth(field, m)}
          className={`w-8 h-8 rounded-lg text-xs font-bold ${(cfg[field] || []).includes(m) ? "bg-canard text-white" : "bg-sand text-muted2"}`}>{m}</button>
      ))}
    </div>
  );

  return (
    <div data-testid="pricing-panel" className="bg-white rounded-2xl shadow-card border border-border p-6 space-y-6 max-w-2xl">
      <div className="flex items-center justify-between">
        <div>
          <p className="font-heading font-bold text-ink">{t("pricing.enable")}</p>
          <p className="text-xs text-muted2">{t("pricing.desc")}</p>
        </div>
        <Switch data-testid="pricing-enabled" checked={cfg.enabled} onCheckedChange={(v) => setCfg({ ...cfg, enabled: v })} />
      </div>

      <div className={cfg.enabled ? "space-y-5" : "space-y-5 opacity-50 pointer-events-none"}>
        <div>
          <label className="text-sm font-bold text-ink">{t("pricing.highSeason")} (%)</label>
          <div className="flex items-center gap-3 mt-1">
            <Input data-testid="high-season-pct" type="number" value={cfg.high_season_pct} onChange={(e) => setCfg({ ...cfg, high_season_pct: parseFloat(e.target.value) || 0 })} className="h-10 w-28 rounded-xl bg-sand" />
            <MonthPicker field="high_season_months" />
          </div>
        </div>
        <div>
          <label className="text-sm font-bold text-ink">{t("pricing.lowSeason")} (%)</label>
          <div className="flex items-center gap-3 mt-1">
            <Input data-testid="low-season-pct" type="number" value={cfg.low_season_pct} onChange={(e) => setCfg({ ...cfg, low_season_pct: parseFloat(e.target.value) || 0 })} className="h-10 w-28 rounded-xl bg-sand" />
            <MonthPicker field="low_season_months" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-bold text-ink">{t("pricing.longDays")}</label>
            <Input type="number" value={cfg.long_duration_days} onChange={(e) => setCfg({ ...cfg, long_duration_days: parseInt(e.target.value) || 0 })} className="h-10 mt-1 rounded-xl bg-sand" />
          </div>
          <div>
            <label className="text-sm font-bold text-ink">{t("pricing.longPct")} (%)</label>
            <Input type="number" value={cfg.long_duration_pct} onChange={(e) => setCfg({ ...cfg, long_duration_pct: parseFloat(e.target.value) || 0 })} className="h-10 mt-1 rounded-xl bg-sand" />
          </div>
        </div>
        <div>
          <label className="text-sm font-bold text-ink">{t("pricing.demandPct")} (%)</label>
          <Input type="number" value={cfg.demand_pct} onChange={(e) => setCfg({ ...cfg, demand_pct: parseFloat(e.target.value) || 0 })} className="h-10 mt-1 rounded-xl bg-sand max-w-[160px]" />
          <p className="text-xs text-muted2 mt-1">{t("pricing.demandHint")}</p>
        </div>
      </div>

      <Button data-testid="pricing-save" onClick={save} disabled={saving} className="rounded-full bg-canard hover:bg-canard-dark font-bold">{saving ? "..." : t("profile.save")}</Button>
    </div>
  );
}
